import { Search } from "lucide-react";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { GlobalSearchKbd } from "./GlobalSearchKbd";
import type { IGlobalSearchTriggerProps } from "./types";
import {
  getSearchShortcutKeycap,
  getSearchShortcutLabel,
} from "./utils/get-search-shortcut-label";

// NOTE: NSAI-6853 — the header's way into the palette, shaped like the search
// field it opens: a "Search…" pill with the shortcut on a keycap, which reads
// ⌘K on a Mac and Ctrl K on Windows / Linux. Below `md` it folds into the
// round icon button it used to be, so the breadcrumb keeps its room. The id is
// what the product tour anchors to (MainTourProvider).
const GlobalSearchTriggerComponent = ({
  isOpen,
  onOpenChange,
}: IGlobalSearchTriggerProps): JSX.Element => {
  const { t } = useTranslation("globalSearch");
  const shortcutLabel = useMemo(() => getSearchShortcutLabel(), []);
  const shortcutKeycap = useMemo(() => getSearchShortcutKeycap(), []);

  const handleClick = useCallback((): void => {
    onOpenChange(!isOpen);
  }, [isOpen, onOpenChange]);

  return (
    <button
      type="button"
      id="sidebar-search"
      data-testid="sidebar-search"
      aria-label={t("trigger_label")}
      aria-keyshortcuts="Meta+K Control+K"
      title={`${t("trigger_label")} (${shortcutLabel})`}
      onClick={handleClick}
      className="group flex h-9 w-9 shrink-0 items-center justify-center gap-2 rounded-full border border-input bg-background-secondary/60 text-foreground-secondary transition-[background-color,border-color,color,transform] duration-200 ease-out hover:border-ring/40 hover:bg-background-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.98] motion-reduce:transition-none motion-reduce:active:scale-100 md:w-56 md:justify-start md:pe-1.5 md:ps-3 lg:w-64"
    >
      <Search className="h-4 w-4 shrink-0" />
      <span className="hidden flex-1 truncate text-start text-sm md:inline">
        {t("trigger_placeholder")}
      </span>
      <GlobalSearchKbd className="hidden md:inline-flex">
        {shortcutKeycap}
      </GlobalSearchKbd>
    </button>
  );
};

export const GlobalSearchTrigger = memo(GlobalSearchTriggerComponent);
